import type { OnChanges, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import type { BackendUpdateErrorResponse } from '@freelancer/datastore';
import type {
  ProjectBookmarksCollection,
  SearchActiveContest,
  SearchActiveProject,
} from '@freelancer/datastore/collections';
import {
  Currency,
  ProjectBookmark,
  ProjectBookmarkType,
} from '@freelancer/datastore/collections';
import type { AmountRange } from '@freelancer/pipes';
import { trackById } from '@freelancer/ui';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { RibbonPlacement } from '@freelancer/ui/ribbon';
import { TagColor, TagSize } from '@freelancer/ui/tag';
import {
  FontColor,
  FontType,
  FontWeight,
  ReadMore,
  TextAlign,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import { UpgradeTagSize, UpgradeType } from '@freelancer/ui/upgrade-tag';
import { isDefined } from '@freelancer/utils';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import { getConvertedRoundedAmount } from './project-contest-card.helper';

@Component({
  selector: 'fl-project-contest-card',
  template: `
    <div class="Container">
      <!-- Left column-->
      <div
        class="Header"
        [class.Header-freeBid]="showFreeBidIcon"
      >
        <div class="Title">
          <fl-heading
            class="Title-text"
            [headingType]="HeadingType.H2"
            [color]="HeadingColor.INHERIT"
            [size]="TextSize.MID"
            [flMarginBottom]="isProject(cardDataInfo) ? 'xxxsmall' : 'small'"
            [flMarginBottomDesktop]="
              isProject(cardDataInfo) ? 'xxsmall' : 'small'
            "
          >
            {{ cardDataInfo.title }}
          </fl-heading>
          <div class="CardPills">
            <fl-tag
              *ngIf="showFreeBidIcon"
              i18n-matTooltip="
                Tooltip indicating that bidding on the project is free
              "
              matTooltip="Bidding on this project is free from April 18 - April 21 to celebrate Easter"
              class="Title-tooltip-label"
              i18n="'Text indicating that bidding is free'"
              [color]="TagColor.DARK_GREEN"
              [flMarginRight]="'xxsmall'"
              [size]="TagSize.SMALL"
              [textTransform]="TextTransform.UPPERCASE"
              [fontWeight]="FontWeight.BOLD"
            >
              Free to bid
            </fl-tag>

            <fl-tag
              *ngIf="isPromoted && isProject(cardDataInfo)"
              i18n-matTooltip="
                Tooltip indicating that this project was recently promoted
              "
              matTooltip="This project has been promoted — now's your chance to bid early and get noticed!"
              class="Title-tooltip-label"
              i18n="'Text indicating that project is promoted'"
              [color]="TagColor.SECONDARY"
              [size]="TagSize.SMALL"
              [textTransform]="TextTransform.UPPERCASE"
              [fontWeight]="FontWeight.BOLD"
            >
              Promoted
            </fl-tag>
          </div>
        </div>
        <div
          class="BudgetUpgradeWrapper"
          [flMarginBottom]="
            isProject(cardDataInfo) ||
            (isContest(cardDataInfo) && upgrades.length > 0)
              ? 'xxsmall'
              : 'small'
          "
        >
          <div
            *ngIf="showBudget && isProject(cardDataInfo)"
            class="BudgetUpgradeWrapper-budget"
            [flMarginRight]="'small'"
            [flMarginBottom]="'xxsmall'"
            [flMarginBottomDesktop]="'xxxsmall'"
          >
            <fl-text
              i18n="Shows the project budget"
              [flMarginRight]="'xxxsmall'"
            >
              Budget
              <ng-container *ngIf="!isConvertedBudget">
                {{
                  cardDataInfo.budget
                    | flCurrency
                      : cardDataInfo.currency.code
                      : { withPrecision: false }
                }}
                <ng-container
                  *ngIf="cardDataInfo.type === ProjectTypeApi.HOURLY"
                >
                  <div class="Hourly">per hour</div>
                </ng-container>
              </ng-container>

              <ng-container *ngIf="budgetRange && isConvertedBudget">
                <span
                  *ngIf="cardDataInfo.type === ProjectTypeApi.FIXED"
                  class="BudgetTooltip"
                  i18n-matTooltip="
                    Shows the project budget in the viewer's currency
                  "
                  matTooltip="~{{
                    budgetRange
                      | flCurrency
                        : viewerCurrency.code
                        : { withPrecision: false }
                  }}"
                >
                  {{
                    cardDataInfo.budget
                      | flCurrency
                        : cardDataInfo.currency.code
                        : { withPrecision: false }
                  }}
                </span>

                <span
                  *ngIf="cardDataInfo.type === ProjectTypeApi.HOURLY"
                  class="BudgetTooltip"
                  i18n-matTooltip="
                    Shows the project's pay rate in the viewer's currency
                  "
                  matTooltip="~{{
                    budgetRange
                      | flCurrency
                        : viewerCurrency.code
                        : { withPrecision: false }
                  }} per hour"
                >
                  {{
                    cardDataInfo.budget
                      | flCurrency
                        : cardDataInfo.currency.code
                        : { withPrecision: false }
                  }}
                  <div class="Hourly">per hour</div>
                </span>
              </ng-container>
            </fl-text>
          </div>

          <fl-project-contest-card-tags
            [upgrades]="upgrades"
            [isContest]="isContest(cardDataInfo)"
            [isProject]="isProject(cardDataInfo)"
            [flMarginBottom]="'xxsmall'"
            [flMarginBottomDesktop]="'xxxsmall'"
          ></fl-project-contest-card-tags>
        </div>
      </div>
      <!-- END Left column-->

      <!-- right column-->
      <div
        class="FlexWrapper"
        [flMarginBottom]="
          showRibbon &&
          ((isProject(cardDataInfo) && !isSealed) || isContest(cardDataInfo))
            ? 'large'
            : 'small'
        "
        [flMarginBottomDesktop]="'none'"
      >
        <div
          *ngIf="hasCounter"
          [flMarginRight]="'small'"
          [flMarginRightDesktop]="'mid'"
        >
          <fl-text
            class="BidEntryData"
            [fontType]="FontType.PARAGRAPH"
            [size]="TextSize.SMALL"
            [sizeDesktop]="TextSize.MID"
          >
            <!-- Project Bids -->
            <div *ngIf="showBudget && isProject(cardDataInfo)">
              <ng-container
                *ngIf="cardDataInfo.bidStats.bidCount > 1"
                i18n="Indicates the number of bids the project has"
              >
                {{ cardDataInfo.bidStats.bidCount }} bids
              </ng-container>
              <ng-container
                *ngIf="cardDataInfo.bidStats.bidCount === 1"
                i18n="Indicates that there is one bid made on a project"
              >
                1 bid
              </ng-container>
            </div>
            <!-- Contest Entries -->
            <div *ngIf="isContest(cardDataInfo)">
              <ng-container
                *ngIf="cardDataInfo.entryCount > 1"
                i18n="Indicates the number of entries a contest has"
              >
                {{ cardDataInfo.entryCount }} entries
              </ng-container>
              <ng-container
                *ngIf="cardDataInfo.entryCount === 1"
                i18n="Indicates that a contest has 1 entry"
              >
                {{ cardDataInfo.entryCount }} entry
              </ng-container>
            </div>
          </fl-text>
        </div>

        <div [class.AverageBidContainer]="showRibbon">
          <ng-container *ngIf="isProject(cardDataInfo)">
            <div
              *ngIf="
                showBudget && !isSealed && cardDataInfo.bidStats.bidCount !== 0
              "
              class="AverageBid"
            >
              <div class="AverageBid-container">
                <fl-text
                  i18n="Average bid"
                  class="AverageBid-amount"
                  [flMarginRightDesktop]="'none'"
                  [flMarginRight]="'xxxsmall'"
                  [fontType]="FontType.PARAGRAPH"
                  [sizeDesktop]="TextSize.MID"
                  [size]="TextSize.SMALL"
                  [textAlignDesktopSmall]="TextAlign.RIGHT"
                  [weight]="FontWeight.BOLD"
                >
                  <ng-container *ngIf="!isConvertedBudget">
                    {{
                      cardDataInfo.bidStats.bidAvg
                        | flCurrency
                          : cardDataInfo.currency.code
                          : { withPrecision: false }
                    }}
                  </ng-container>
                  <span
                    *ngIf="
                      isConvertedBudget && equivalentAverageBidInViewerCurrency
                    "
                    class="AverageBidContainer-tooltip"
                    flTrackingLabel="AverageProjectBidTooltip"
                    matTooltip="~{{
                      equivalentAverageBidInViewerCurrency
                        | flCurrency
                          : viewerCurrency.code
                          : { withPrecision: false }
                    }}"
                    [flMarginRight]="'xxxsmall'"
                  >
                    {{
                      cardDataInfo.bidStats.bidAvg
                        | flCurrency
                          : cardDataInfo.currency.code
                          : { withPrecision: false }
                    }}
                  </span>
                  <ng-container
                    *ngIf="cardDataInfo.type === ProjectTypeApi.HOURLY"
                  >
                    <div class="Hourly">per hour</div>
                  </ng-container>
                </fl-text>
              </div>
              <p
                class="text-xxxsmall AverageBid-copy desktop_small:text-right"
                i18n="Average bid copy"
              >
                average bid
              </p>
            </div>
          </ng-container>
          <ng-container *ngIf="isContest(cardDataInfo)">
            <fl-text
              class="ContestPrize"
              [flMarginRightDesktop]="'none'"
              [flMarginRight]="'xxxsmall'"
              [flShowDesktop]="showRibbon"
              [fontType]="FontType.PARAGRAPH"
              [sizeDesktop]="TextSize.MID"
              [size]="TextSize.SMALL"
              [textAlignDesktopSmall]="TextAlign.RIGHT"
              [weight]="FontWeight.BOLD"
            >
              {{
                cardDataInfo.prize
                  | flCurrency
                    : cardDataInfo.currency.code
                    : { withPrecision: false }
              }}
            </fl-text>
          </ng-container>
          <div
            *ngIf="
              isProject(cardDataInfo) &&
              cardDataInfo.bidStats.bidCount === 0 &&
              !isSealed
            "
            class="Ribbon"
          >
            <ng-container [ngTemplateOutlet]="ribbon"></ng-container>
          </div>

          <div
            *ngIf="isContest(cardDataInfo) && cardDataInfo.entryCount === 0"
            class="Ribbon"
          >
            <ng-container [ngTemplateOutlet]="ribbon"></ng-container>
            <ng-container *ngIf="isContest(cardDataInfo)">
              <fl-text
                class="ContestPrizeLeft"
                [flHideDesktop]="true"
                [flMarginRightDesktop]="'none'"
                [flMarginRight]="'xxxsmall'"
                [fontType]="FontType.PARAGRAPH"
                [sizeDesktop]="TextSize.MID"
                [size]="TextSize.SMALL"
                [textAlignDesktopSmall]="TextAlign.RIGHT"
                [weight]="FontWeight.BOLD"
              >
                {{
                  cardDataInfo.prize
                    | flCurrency
                      : cardDataInfo.currency.code
                      : { withPrecision: false }
                }}
              </fl-text>
            </ng-container>
          </div>

          <ng-template #ribbon>
            <fl-ribbon
              class="RibbonLeft"
              [flMarginRight]="isContest(cardDataInfo) ? 'large' : 'none'"
              [flMarginRightDesktop]="'none'"
              [placement]="RibbonPlacement.LEFT"
              [placementDesktop]="RibbonPlacement.RIGHT"
            >
              <ng-container
                i18n="First bid"
                *ngIf="isProject(cardDataInfo)"
              >
                BE FIRST TO BID
              </ng-container>
              <ng-container
                i18n="First entry"
                *ngIf="isContest(cardDataInfo)"
              >
                BE FIRST TO ENTER
              </ng-container>
            </fl-ribbon>
          </ng-template>

          <fl-upgrade-tag
            *ngIf="isProject(cardDataInfo) && isSealed"
            class="Sealed"
            i18n-matTooltip="Tooltip message for Sealed project upgrade"
            matTooltip="Details of the bids placed in this project can only be seen by the client and their collaborators."
            [size]="UpgradeTagSize.SMALL"
            [upgradeType]="UpgradeType.SEALED"
          ></fl-upgrade-tag>
        </div>
      </div>
      <!-- END right column-->
    </div>
    <!-- END Container-->

    <!-- Description and skills -->
    <fl-text
      [displayLineBreaks]="displayLineBreaks"
      [fontType]="FontType.PARAGRAPH"
      [maxLines]="3"
      [readMore]="ReadMore.LINK"
      [flMarginBottom]="'xxsmall'"
      [flMarginBottomDesktop]="'xsmall'"
      (expand)="handleExpand($event)"
    >
      {{ cardDataInfo.description }}
    </fl-text>

    <div
      class="SkillsWrapper"
      [flMarginBottom]="'xsmall'"
      [flHideDesktop]="true"
    >
      <fl-tag
        *ngFor="
          let skill of cardDataInfo.skills | slice: 0 : displayLimit;
          trackBy: trackById
        "
        class="SkillsWrapper-skill"
        [borderless]="true"
      >
        {{ skill.name }}
      </fl-tag>

      <span
        class="text-color-inherit text-xsmall MoreSkills"
        *ngIf="cardDataInfo.skills.length > displayLimit"
        i18n="Number of projects's extra skills"
      >
        {{ cardDataInfo.skills.length - displayLimit }} more
      </span>
    </div>

    <div
      class="SkillsWrapper"
      [flMarginBottom]="'xsmall'"
      [flShowDesktop]="true"
    >
      <fl-tag
        *ngFor="
          let skill of cardDataInfo.skills | slice: 0 : displayLimitDesktop;
          trackBy: trackById
        "
        class="SkillsWrapper-skill"
        [borderless]="true"
      >
        {{ skill.name }}
      </fl-tag>

      <span
        class="text-color-inherit text-xsmall MoreSkills"
        *ngIf="cardDataInfo.skills.length > displayLimitDesktop"
        i18n="Number of projects's extra skills"
      >
        {{ cardDataInfo.skills.length - displayLimitDesktop }} more
      </span>
    </div>

    <div class="Footer">
      <div
        class="Footer-wrapper"
        [flMarginBottom]="
          isProject(cardDataInfo) && userHasBid ? 'xxsmall' : 'none'
        "
      >
        <div
          class="FlexWrapper"
          [flMarginBottom]="'xxxsmall'"
          [flMarginBottomDesktop]="'none'"
        >
          <div
            matTooltip="Overall rating given to the client by previous freelancers."
            i18n-matTooltip="Tooltip message for client rating"
          >
            <fl-rating
              class="ClientRating"
              [readOnly]="true"
              [control]="ratingControl"
              [inline]="true"
              [flMarginRight]="'xxsmall'"
            ></fl-rating>
          </div>

          <fl-review-count
            i18n-matTooltip="Tooltip message for client review count"
            matTooltip="Total number of reviews the client has received."
            [counter]="cardDataInfo.clientReputation?.reviews || 0"
          ></fl-review-count>
        </div>

        <div
          class="FlexWrapper"
          [flMarginBottom]="'xxxsmall'"
          [flMarginBottomDesktop]="'none'"
        >
          <div class="UrgentProject">
            <!-- Time left indicator -->
            <fl-text
              *ngIf="hasSubmissionTimePassed === false"
              i18n="Remaining time"
              [fontType]="FontType.SPAN"
              [flMarginRight]="isUrgent ? 'xxsmall' : 'none'"
            >
              <fl-relative-time
                [color]="FontColor.INHERIT"
                [continueAfterTimePassed]="false"
                [date]="
                  isProject(cardDataInfo)
                    ? cardDataInfo.timeSubmitted
                    : (cardDataInfo?.timePosted ?? 0)
                "
                [size]="TextSize.INHERIT"
                [strict]="true"
                [suffix]="false"
              ></fl-relative-time>
              ago
            </fl-text>
            <ng-container *ngIf="hasSubmissionTimePassed">
              <fl-text
                *ngIf="isProject(cardDataInfo)"
                i18n="Indicator that the project is closed for bidding"
                [flMarginRight]="isUrgent ? 'xxsmall' : 'none'"
              >
                Bidding period closed
              </fl-text>
              <fl-text
                *ngIf="isContest(cardDataInfo)"
                i18n="Indicator that the contest is closed for entries"
                [flMarginRight]="isUrgent ? 'xxsmall' : 'none'"
              >
                Entry period closed
              </fl-text>
            </ng-container>

            <!-- Urgent upgrade -->
            <ng-container *ngIf="isUrgent">
              <fl-upgrade-tag
                class="Urgent"
                i18n-matTooltip="Tooltip message for Urgent project upgrade"
                matTooltip="Bidding is only open for 24 hours."
                [size]="UpgradeTagSize.SMALL"
                [upgradeType]="UpgradeType.URGENT"
              ></fl-upgrade-tag>
            </ng-container>
          </div>
          <app-project-bookmark-button
            *ngIf="bookmark"
            class="Bookmark"
            [colorSubscribed]="IconColor.SECONDARY"
            [project]="cardDataInfo"
            [type]="bookmarkType"
            [bookmark]="bookmark"
            (error)="bookmarkError.emit($event)"
            (success)="bookmarkSuccess.emit()"
          ></app-project-bookmark-button>
        </div>
      </div>
      <div
        *ngIf="isProject(cardDataInfo) && userHasBid"
        class="Footer-section"
      >
        <div class="BidIndicator">
          <fl-icon
            [flMarginRight]="'xxxsmall'"
            [name]="'ui-check-in-circle-v2'"
            [size]="IconSize.SMALL"
          ></fl-icon>
          <p
            class="text-xsmall"
            i18n="Bid indicator text"
          >
            You have already bid on this project
          </p>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./project-contest-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectContestCardComponent implements OnChanges, OnInit {
  FontColor = FontColor;
  FontWeight = FontWeight;
  FontType = FontType;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  IconColor = IconColor;
  IconSize = IconSize;
  ProjectTypeApi = ProjectTypeApi;
  ReadMore = ReadMore;
  RibbonPlacement = RibbonPlacement;
  TagColor = TagColor;
  TagSize = TagSize;
  TextAlign = TextAlign;
  TextSize = TextSize;
  TextTransform = TextTransform;
  trackById = trackById;
  UpgradeTagSize = UpgradeTagSize;
  UpgradeType = UpgradeType;
  ProjectBookmarkType = ProjectBookmarkType;

  @Input() cardDataInfo: SearchActiveContest | SearchActiveProject;
  @Input() isFreeBidEnabled = false;
  @Input() userHasBid?: boolean;
  @Input() viewerCurrency: Currency;
  @Input() bookmarkType: ProjectBookmarkType;
  @Input() bookmark?: ProjectBookmark;
  @Input() showBudget = true;

  @Output() bookmarkError = new EventEmitter<
    BackendUpdateErrorResponse<ProjectBookmarksCollection>
  >();
  @Output() bookmarkSuccess = new EventEmitter<void>();

  ratingControl: FormControl<number>;
  displayLineBreaks = false;
  equivalentAverageBidInViewerCurrency?: number;
  hasCounter = false;
  isPromoted = false;

  readonly displayLimit = 2;
  readonly displayLimitDesktop = 5;
  readonly promotionThresholdHours = 18;

  upgrades: any;
  isSealed: boolean;
  isUrgent: boolean;
  showRibbon: boolean;
  budgetRange: AmountRange;
  isConvertedBudget: boolean;
  hasSubmissionTimePassed?: boolean;
  showFreeBidIcon = false;

  handleExpand(isExpanded: boolean): void {
    this.displayLineBreaks = isExpanded;
  }

  ngOnInit(): void {
    if (this.cardDataInfo.upgradeNames.includes(UpgradeType.SEALED)) {
      this.isSealed = true;
    } else {
      this.isSealed = false;
    }

    if (this.cardDataInfo.upgradeNames.includes(UpgradeType.URGENT)) {
      this.isUrgent = true;
    } else {
      this.isUrgent = false;
    }

    // Check if project was bumped in the past 18 hours
    if (this.isProject(this.cardDataInfo) && this.cardDataInfo.lastBumpTime) {
      const HOURS_IN_MILLISECONDS = 1000 * 60 * 60;
      const HOURS_AGO =
        (Date.now() - this.cardDataInfo.lastBumpTime) / HOURS_IN_MILLISECONDS;
      this.isPromoted = HOURS_AGO <= this.promotionThresholdHours;
    }

    this.ratingControl = new FormControl(
      this.cardDataInfo.clientReputation?.rating ?? 0,
      { nonNullable: true },
    );

    if (this.isProject(this.cardDataInfo)) {
      if (this.cardDataInfo.bidStats.bidCount === 0) {
        this.showRibbon = true;
      } else {
        this.showRibbon = false;
      }

      this.hasSubmissionTimePassed =
        this.cardDataInfo.bidTimeLeft <= Date.now();

      this.upgrades = this.cardDataInfo.upgradeNames.filter(
        upgrade =>
          upgrade !== UpgradeType.SEALED && upgrade !== UpgradeType.URGENT,
      );
    }

    if (this.isContest(this.cardDataInfo)) {
      if (this.cardDataInfo.entryCount === 0) {
        this.showRibbon = true;
      } else {
        this.showRibbon = false;
      }

      this.hasSubmissionTimePassed = isDefined(this.cardDataInfo.timeEnded)
        ? this.cardDataInfo.timeEnded <= Date.now()
        : undefined;

      this.upgrades = this.cardDataInfo.upgradeNames.filter(
        upgrade => upgrade !== UpgradeType.URGENT,
      );
    }

    this.hasCounter = this.isProject(this.cardDataInfo)
      ? this.cardDataInfo.bidStats.bidCount > 0
      : this.isContest(this.cardDataInfo)
        ? this.cardDataInfo.entryCount > 0
        : false;
  }

  ngOnChanges(): void {
    if (this.isProject(this.cardDataInfo)) {
      if (
        this.viewerCurrency?.exchangeRate &&
        this.cardDataInfo.currency.exchangeRate
      ) {
        this.budgetRange = {
          minimum: getConvertedRoundedAmount(
            this.cardDataInfo.budget.minimum,
            this.cardDataInfo.currency.exchangeRate,
            this.viewerCurrency.exchangeRate,
          ),
          maximum: this.cardDataInfo.budget.maximum
            ? getConvertedRoundedAmount(
                this.cardDataInfo.budget.maximum,
                this.cardDataInfo.currency.exchangeRate,
                this.viewerCurrency.exchangeRate,
              )
            : undefined,
        };

        this.isConvertedBudget =
          this.cardDataInfo.currency.id !== this.viewerCurrency.id;

        this.equivalentAverageBidInViewerCurrency = this.cardDataInfo.bidStats
          .bidAvg
          ? getConvertedRoundedAmount(
              this.cardDataInfo.bidStats.bidAvg,
              this.cardDataInfo.currency.exchangeRate,
              this.viewerCurrency.exchangeRate,
            )
          : undefined;
      }

      this.showFreeBidIcon =
        this.isFreeBidEnabled &&
        !this.cardDataInfo.enterpriseIds?.length &&
        !this.cardDataInfo.isInsourceProject;
    }
  }

  isProject(
    cardDataInfo: SearchActiveContest | SearchActiveProject,
  ): cardDataInfo is SearchActiveProject {
    return (
      'budget' in cardDataInfo &&
      'bidStats' in cardDataInfo &&
      'bidPeriod' in cardDataInfo &&
      'bidTimeLeft' in cardDataInfo
    );
  }

  isContest(
    cardDataInfo: SearchActiveContest | SearchActiveProject,
  ): cardDataInfo is SearchActiveContest {
    return (
      'prize' in cardDataInfo &&
      'timePosted' in cardDataInfo &&
      'entryCount' in cardDataInfo
    );
  }
}
